import IcMenu from '@assets/details-camera-icons/IcMenu';
import IcSetting from '@assets/details-camera-icons/IcSetting';
import BaseCheckbox from '@components/BaseCheckbox';
import { BaseTextField } from '@components/BaseTextField';
import BreadCrumbs from '@components/breadcumbs/BreadCumbs';
import IOSSwitch from '@components/IOSSwitch';
import LoadingBreadCrumbs from '@components/skeleton/LoadingBreadCrumbs';
import StyledAutocomplete from '@components/styled-autocomplete';
import { ContraventionReasonTranslations, GeoZoneCoordinates, GeoZones, Location } from '@EcamModel/model';
import { PhotoSnapShotByPTZ } from '@EcamModel/model/CameraView';
import { Zones } from '@EcamModel/model/Zones';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Fade,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerValue } from '@pages/cameras/add-new';
import color from '@theme/Colors';
import moment from 'moment';
import { Controller, FormProvider } from 'react-hook-form';
import { BsQuestion } from 'react-icons/bs';
import {
    contraventionReasonEV,
    contraventionReasonNoRoadway,
    detectModes,
    GeoZoneGracePeriod,
    HeadingSection,
} from '../../../../AddGeoZones';
import { Mode } from '../../../../CameraInformation';
import GracePeriods from '../../../../GracePeriods';
import OperationalHours from '../../../../OperationalHours';
import FramePolygonGeoOverview from '../../../components/FramePolygonGeoOverview';
import useAddGeoZones from '../hook/useAddGeoZones';
import FormAddGeoZonesPtzOverviewPage from './FormAddGeoZonesPtzOverviewPage';
import { updateGracePeriods } from '@pages/cameras/details/utils/gracePeriodUtils';
import { useCallback, useState } from 'react';
import { renderDetectModesOption } from './EditGeoZonesPTZ';

export type AddGzOverviewProps = {
    condition: boolean;
    isAddGz?: boolean;
    presetName?: string;
    gotoPointId?: number;
    photoSnapShotByPTZ?: PhotoSnapShotByPTZ;
};

export default function AddGeoZonesCameraPTZ() {
    const {
        setStartDate,
        handleRadioChangeCameraType,
        updateOperationHours,
        setIsOverlap,
        handleAddGeoZoneOverview,
        shouldDisableButton,
        handleNavigateButton,
        loading,
        prevGeoZonePointsOverviewPage,
        conditionBreadcrumbs,
        errors,
        methods,
        selectedCameraType,
        contraventionAutocomplete,
        detectModeAutocomplete,
        gracePeriodForm,
        operationHours,
        isLoadingSave,
        startDate,
        isHasCameraViewZoomId,
        typeDetailsIncludesCameraViewZoomId,
        isTypeOverview,
        imageElementPTZ,
        loadingListGzByView,
        locationAutocomplete,
        zoneAutocomplete,
        zones,
        setZones,
    } = useAddGeoZones({});

    const [addGzOverviewProps, setAddGzOverview] = useState<AddGzOverviewProps>({
        condition: false,
        presetName: '',
        photoSnapShotByPTZ: {} as PhotoSnapShotByPTZ,
    });
    const [loadingButtonType, setLoadingButtonType] = useState<number | null>(null);

    const isAddGzOverview = isTypeOverview && !isHasCameraViewZoomId;
    const setValue = methods.setValue as (name: string, value: any, options?: Record<string, boolean>) => void;

    const handleDeleteGeoZonePoints = () => {
        setValue('geoZonePoints', [] as GeoZoneCoordinates[], {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        });
    };

    const onChangeView = useCallback(
        (condition: boolean, presetName?: string, photoSnapShotByPTZ?: PhotoSnapShotByPTZ, gotoPointId?: number) => {
            setAddGzOverview({ condition, presetName, photoSnapShotByPTZ, gotoPointId: gotoPointId });
        },
        []
    );

    return (
        <Stack mt={1} width={'100%'}>
            <Stack mb={!isHasCameraViewZoomId ? 3 : 0}>
                <Typography variant="h3" mb={1}>
                    Add geo-zones
                </Typography>
                {loading ? <LoadingBreadCrumbs /> : <BreadCrumbs breadcrumbs={conditionBreadcrumbs} />}
            </Stack>
            {!isHasCameraViewZoomId && (
                <Grid container xs={12} alignItems="center">
                    <Grid xs={6} lg={2.5}>
                        <Typography variant="h5">Select geo-zone to add:</Typography>
                    </Grid>
                    <Grid
                        xs={6}
                        lg={9.5}
                        sx={{
                            textAlign: {
                                xs: 'end',
                                lg: 'start',
                            },
                        }}
                    >
                        <FormControl>
                            <RadioGroup row value={selectedCameraType} onChange={handleRadioChangeCameraType}>
                                <FormControlLabel
                                    sx={{
                                        mr: 2,
                                        '.MuiFormControlLabel-label': {
                                            mt: '3px !important',
                                        },
                                    }}
                                    control={<Radio value={0} />}
                                    label="Geo-zones overview"
                                />
                                <FormControlLabel
                                    sx={{
                                        '.MuiFormControlLabel-label': {
                                            mt: '3px !important',
                                        },
                                    }}
                                    control={<Radio value={1} />}
                                    label="Geo-zones detail"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            <FormProvider {...methods}>
                <Box>
                    {isTypeOverview && !isHasCameraViewZoomId && (
                        <FormAddGeoZonesPtzOverviewPage methods={methods} onChangeView={onChangeView} />
                    )}

                    {typeDetailsIncludesCameraViewZoomId && (
                        <Fade in={typeDetailsIncludesCameraViewZoomId} timeout={800}>
                            <Box mt={3}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                    <Controller
                                        name="contraventionReasonTranslations"
                                        control={methods.control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: 'Please select a Contravention reason.',
                                            },
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <StyledAutocomplete
                                                {...field}
                                                {...contraventionAutocomplete}
                                                isRequired
                                                getOptionLabel={(o) => o.Summary}
                                                label="Contravention reason"
                                                value={field.value}
                                                placeholder="Select reason"
                                                disabledAllOption
                                                onChange={(c: ContraventionReasonTranslations) => {
                                                    const currentGracePeriods = methods.watch()
                                                        .gracePeriods as Required<GeoZoneGracePeriod>[];
                                                    const updatedGracePeriods = updateGracePeriods({
                                                        currentGracePeriods,
                                                        isZeroGracePeriod: [
                                                            contraventionReasonEV,
                                                            contraventionReasonNoRoadway,
                                                        ].includes(c.ContraventionReasonId),
                                                    });

                                                    setValue('gracePeriods', updatedGracePeriods);
                                                    setValue('contraventionReasonTranslations', c!, {
                                                        shouldValidate: true,
                                                        shouldTouch: true,
                                                        shouldDirty: true,
                                                    });
                                                    methods.clearErrors();
                                                }}
                                                wrapperWidth="100%"
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="detectMode"
                                        control={methods.control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: 'Please select a detectode.',
                                            },
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <StyledAutocomplete
                                                {...field}
                                                {...detectModeAutocomplete}
                                                isRequired
                                                getOptionLabel={(o) => o.Label}
                                                customRenderOption={(_, option) =>
                                                    renderDetectModesOption(option, detectModes)
                                                }
                                                label="Detect mode"
                                                value={field.value}
                                                disabledAllOption
                                                onChange={(c) => {
                                                    field.onChange(c);
                                                }}
                                                wrapperWidth="100%"
                                            />
                                        )}
                                    />
                                    <div>
                                        <Controller
                                            name="isActive"
                                            control={methods.control}
                                            render={({ field, fieldState: { error } }) => (
                                                <IOSSwitch
                                                    {...field}
                                                    label={
                                                        <Typography sx={{ minWidth: '55px' }}>
                                                            {!!field.value ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    }
                                                    iosSwitchProps={{
                                                        checked: field.value,
                                                        onChange(event, checked) {
                                                            field.onChange(event);
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                </Stack>

                                {/* checkbox ExemptVRNs */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent={'space-between'}
                                    width="100%"
                                    mt={2}
                                >
                                    <Stack direction="row" alignItems="center">
                                        <Controller
                                            name="isExemptVRNs"
                                            control={methods.control}
                                            render={({ field, fieldState: { error } }) => (
                                                <FormControlLabel
                                                    control={
                                                        <BaseCheckbox
                                                            checked={field.value}
                                                            onChange={(_, checked) => {
                                                                field.onChange(checked);
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography ml={0.5} mt={0.3}>
                                                            Exempt VRNs on Permit lists
                                                        </Typography>
                                                    }
                                                    sx={{ ml: 0 }}
                                                />
                                            )}
                                        />
                                    </Stack>
                                </Stack>
                                {/* end checkbox ExemptVRNs  */}

                                {/* Location and Zone */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={2}
                                    mt={2}
                                >
                                    <Controller
                                        name="location"
                                        control={methods.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <StyledAutocomplete
                                                {...field}
                                                {...locationAutocomplete}
                                                getOptionLabel={(o) => (o as Location)?.Name}
                                                label="Location"
                                                value={field.value}
                                                placeholder="Select location"
                                                disabledAllOption
                                                onChange={(l: Location) => {
                                                    setValue('location', l!, {
                                                        shouldValidate: true,
                                                        shouldTouch: true,
                                                        shouldDirty: true,
                                                    });

                                                    // Set zones from the selected location (already merged if applicable)
                                                    setZones(l?.Zones || []);

                                                    // Auto-select camera's zone if it's in the list, otherwise select first zone
                                                    if (l?.Zones && l.Zones.length > 0) {
                                                        const cameraZone = l.Zones.find(
                                                            (z) => z.Id === methods.getValues('zone')?.Id
                                                        );
                                                        setValue('zone', cameraZone || l.Zones[0]);
                                                    } else {
                                                        setValue('zone', undefined as any);
                                                    }
                                                }}
                                                wrapperWidth="100%"
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="zone"
                                        control={methods.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <StyledAutocomplete
                                                {...field}
                                                {...zoneAutocomplete}
                                                getOptionLabel={(o) =>
                                                    `${(o as Zones)?.PublicName} (ZoneID ${(o as Zones)?.Id})`
                                                }
                                                label="Zone"
                                                value={field.value}
                                                placeholder="Select zone"
                                                disabledAllOption
                                                disabled={!methods.watch().location}
                                                onChange={(z: Zones) => {
                                                    setValue('zone', z!, {
                                                        shouldValidate: true,
                                                        shouldTouch: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                wrapperWidth="100%"
                                            />
                                        )}
                                    />
                                </Stack>
                                {/* end Location and Zone */}

                                <>
                                    {/* Draw angle box */}
                                    <HeadingSection icon={<IcMenu />} heading="Zone marking" />
                                    <Controller
                                        name="geoZonePoints"
                                        control={methods.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <FramePolygonGeoOverview
                                                {...field}
                                                imageElement={loadingListGzByView ? undefined : imageElementPTZ!}
                                                mode={Mode.create}
                                                prevGeoZone={prevGeoZonePointsOverviewPage as GeoZoneCoordinates[][]}
                                                selectedGeoZone={{} as GeoZones}
                                                onChangeCurrent={(points) => {
                                                    setValue('currentgeoZonePoints', points, {
                                                        shouldValidate: true,
                                                        shouldTouch: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                onChangeMulti={(multiPoints) => {
                                                    setValue('multigeoZonePoints', multiPoints, {
                                                        shouldValidate: true,
                                                        shouldTouch: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                onDelete={handleDeleteGeoZonePoints}
                                                multiPolygon
                                            />
                                        )}
                                    />
                                </>

                                {/* Operational periods */}
                                <HeadingSection icon={<IcSetting />} heading="Time setting" />
                                <Stack
                                    p={1.7}
                                    sx={{
                                        border: '1px solid #DDDDDD',
                                        borderWidth: '0px 1px 1px 1px',
                                        borderRadius: '0px 0px 4px 4px',
                                    }}
                                    spacing={2}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                        sx={{ borderBottom: '1px solid #DDDDDD', pb: 1.7 }}
                                    >
                                        <Typography>
                                            Effective from<span style={{ color: 'red' }}>*</span>:
                                        </Typography>
                                        <DesktopDatePicker
                                            value={startDate}
                                            format="DD/MM/YYYY"
                                            shouldDisableDate={(date) =>
                                                validateDatePickerValue(moment(date).toDate()) !== null
                                            }
                                            onChange={(e) => setStartDate(e!)}
                                            slotProps={{
                                                textField: {
                                                    inputProps: {
                                                        readOnly: true,
                                                    },
                                                    sx: {
                                                        width: 250,
                                                    },
                                                },
                                            }}
                                        />
                                    </Stack>
                                    <Stack sx={{ borderBottom: '1px solid #DDDDDD', pb: 2 }} spacing={2}>
                                        <Typography>
                                            Grace period setting<span style={{ color: 'red' }}>*</span>:
                                        </Typography>
                                        <GracePeriods
                                            gracePeriods={gracePeriodForm}
                                            control={methods.control}
                                            errors={errors}
                                            watch={methods.watch}
                                        />
                                    </Stack>

                                    {operationHours.map((item, index, arr) => {
                                        return (
                                            <OperationalHours
                                                geoZoneHourConfigWithTimeRanges={item as any}
                                                geoZoneGracePeriod={methods.watch().gracePeriods}
                                                onChange={(item, error) => {
                                                    const operationHours = {
                                                        Weekday: item.Weekday,
                                                        TimeRanges: item.TimeRanges,
                                                        AllDay: item.AllDay,
                                                        Status: item.Status,
                                                    };
                                                    updateOperationHours(index, operationHours);

                                                    setIsOverlap(error);
                                                }}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Fade>
                    )}
                </Box>
            </FormProvider>

            {/* Button */}
            <Stack mt={3} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Button
                    variant="cancel"
                    sx={{ flexBasis: 130 }}
                    disabled={isLoadingSave}
                    onClick={handleNavigateButton}
                >
                    Cancel
                </Button>

                <Stack direction={'row'} spacing={2} justifyContent="flex-end">
                    {isTypeOverview ? (
                        [1, 0].map((e) => {
                            const isAddGz = !!e;
                            const isCurrentLoading = loadingButtonType === e;

                            return (
                                <LoadingButton
                                    key={e}
                                    sx={{
                                        minWidth: 130,
                                    }}
                                    loading={isCurrentLoading}
                                    loadingPosition="center"
                                    variant="contained"
                                    disabled={
                                        shouldDisableButton() ||
                                        (loadingButtonType !== null && loadingButtonType !== e) ||
                                        (isAddGzOverview && addGzOverviewProps.condition)
                                    }
                                    onClick={() => {
                                        setLoadingButtonType(e);
                                        handleAddGeoZoneOverview({ ...addGzOverviewProps, isAddGz })().finally(() => {
                                            setLoadingButtonType(null);
                                        });
                                    }}
                                >
                                    Save {isAddGz && `& add geo zone`}
                                </LoadingButton>
                            );
                        })
                    ) : (
                        <LoadingButton
                            sx={{
                                minWidth: 130,
                            }}
                            loading={isLoadingSave}
                            loadingPosition="center"
                            variant="contained"
                            disabled={shouldDisableButton() || isLoadingSave}
                            onClick={handleAddGeoZoneOverview(addGzOverviewProps)}
                        >
                            Save
                        </LoadingButton>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}
